<template>
    <div class="p-1 flex flex-col h-full w-96" @click="activateFocus">
        <div class="flex flex-col mb-2 ml-2">
            <div class="flex gap-2">
                <account-link tabindex="-1" class="text-lg text-primary" :account-no="pickerOption.accountNo">{{ pickerOption.accountNo }}</account-link>
                <div class="grow"/>
                <span class="">Saldo</span>
                <span class="text-lg">{{ formatCurrency(ledgerAccount?.outgoingBalance || 0) }}</span>
            </div>
            <div class="text-lg">{{ pickerOption.displayName }}</div>
        </div>
        <div class="tab-headers mt-2">
            <button
                ref="descriptionTabHeader outline-none"
                :class="{selected: selectedTab === Tab.DESCRIPTION}"
                class="tab-header rounded-tl mb-0"
                :tabindex="isFocused ? 0 : -1"
                @click="selectedTab = Tab.DESCRIPTION"
            >
                Beskrivelse
            </button>
            <button
                :class="{selected: selectedTab === Tab.TRANSACTIONS}"
                class="tab-header rounded-tl mb-0"
                :tabindex="isFocused ? 0 : -1"
                @click="selectedTab = Tab.TRANSACTIONS"
            >
                Siste bevegelser
            </button>
        </div>
        <div class="rounded-b rounded-tr p-4 bg-base-100 border h-full">
            <div v-if="selectedTab === Tab.DESCRIPTION">
                <p class="leading-tight">{{ bookeepingAccount.description }}</p>
            </div>
            <div v-if="selectedTab === Tab.TRANSACTIONS">
                TODO
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {AccountOption, FOCUS_SIDE} from '@/account-picker/accountPicker.service'
import {computed, nextTick, onMounted, ref, watch} from 'vue'
import _ from 'lodash'
import {getGeneralLedger} from '@/genleg/ledger.service'
import {LedgerAccount} from '@/types/reports/LedgerAccount'
import {format, startOfYear} from 'date-fns'
import AccountLink from '@/bookkeeping-account/AccountLink.vue'
import {findAccountByNo} from '@/bookkeeping-account/bookkeepingAccount.service'
import {formatCurrency} from '@/services/formatter.service'
import {HTMLButtonElement} from 'happy-dom'

const descriptionTabHeader = ref<HTMLButtonElement | null>(null)

const props = defineProps({
    pickerOption: {
        type: Object as () => AccountOption,
        required: true
    }
})

enum Tab {
    DESCRIPTION = 'DESCRIPTION',
    TRANSACTIONS = 'TRANSACTIONS',
}

const selectedTab = ref<Tab>(Tab.DESCRIPTION)

const ledgerAccount = ref<LedgerAccount | undefined>()
const lazyAccount = ref<AccountOption | null>(null)

const focusSide = defineModel<FOCUS_SIDE>('focusSide', {required: true})
const isFocused = computed({
    get: () => focusSide.value === FOCUS_SIDE.RIGHT,
    set: (value) => focusSide.value = value ? FOCUS_SIDE.RIGHT : FOCUS_SIDE.LEFT,
})

const activateFocus = () => {
    isFocused.value = true
    nextTick(() => {
        descriptionTabHeader.value?.focus()
    })
}

const bookeepingAccount = computed(() => {
    return findAccountByNo(props.pickerOption.accountNo)
})

const fetchLedger = async () => {
    const ledger = await getGeneralLedger({
        accountNo: props.pickerOption.accountNo, fromDate: format(startOfYear(new Date()), 'yyyy-MM-dd'),
        toDate: format(new Date(), 'yyyy-MM-dd')
    })
    ledgerAccount.value = ledger?.ledgerAccounts[0]
}

const fetchLazyAccount = _.debounce(async () => {
    lazyAccount.value = props.pickerOption
    await fetchLedger()
}, 200, {trailing: true})

watch(() => props.pickerOption, fetchLazyAccount)
onMounted(fetchLazyAccount)
defineExpose({
    activateFocus
})

</script>

<style scoped>
@reference "@/index.css";
@reference "tailwindcss";

.tab-headers {
    @apply flex w-fit text-lg bg-none;

    .tab-header {
        @apply cursor-pointer px-4 py-1;
        @apply shadow-sm;

        &.selected {
            @apply bg-base-100;
            @apply shadow-none;
            @apply text-primary;
        }
    }

}
</style>